import { formatCurrency, formatSharesRange } from "@/lib/constants";

interface OfferTemplateData {
  salaryBase: string | number;
  equityShares: number | null;
  signingBonus: string | null;
  performanceBonus: string | null;
  performanceBonusShares: number | null;
  performanceBonusSharesMax: number | null;
  offerLetterTemplate: string | null;
  allowPaymixAdjustment?: boolean;
  hiringManagerName?: string | null;
}

interface OfferContext {
  candidateName: string;
  jobTitle: string;
}

/**
 * Processes an offer letter template: evaluates conditionals and replaces variables.
 * Shared across offer-preview, offer-view, and offer-templates.
 */
export function renderOfferTemplate(offer: OfferTemplateData, context: OfferContext): string {
  const hasEquity = (offer.equityShares ?? 0) > 0;

  const conditionals: Record<string, boolean> = {
    signingBonus: !!(offer.signingBonus && parseFloat(String(offer.signingBonus)) > 0),
    performanceBonus: !!(offer.performanceBonus && parseFloat(String(offer.performanceBonus)) > 0),
    equityShares: hasEquity,
    performanceBonusShares: (offer.performanceBonusShares ?? 0) > 0,
    allowPaymixAdjustment: !!(offer.allowPaymixAdjustment && hasEquity),
  };

  const processed = (offer.offerLetterTemplate || "").replace(
    /\{\{#if (\w+)\}\}([\s\S]*?)\{\{\/if\}\}/g,
    (_, key, content) => (conditionals[key] ? content : ""),
  );

  return processed
    .replace(/\{\{candidateName\}\}/g, context.candidateName)
    .replace(/\{\{jobTitle\}\}/g, context.jobTitle)
    .replace(/\{\{salaryBase\}\}/g, formatCurrency(offer.salaryBase))
    .replace(/\{\{equityShares\}\}/g, offer.equityShares?.toLocaleString("pt-BR") || "")
    .replace(/\{\{signingBonus\}\}/g, offer.signingBonus ? formatCurrency(offer.signingBonus) : "")
    .replace(
      /\{\{performanceBonus\}\}/g,
      offer.performanceBonus ? formatCurrency(offer.performanceBonus) : "",
    )
    .replace(/\{\{salaryBaseValue\}\}/g, String(parseFloat(String(offer.salaryBase)) || 0))
    .replace(/\{\{equitySharesValue\}\}/g, String(offer.equityShares || 0))
    .replace(/\{\{signingBonusValue\}\}/g, String(parseFloat(String(offer.signingBonus)) || 0))
    .replace(
      /\{\{performanceBonusValue\}\}/g,
      String(parseFloat(String(offer.performanceBonus)) || 0),
    )
    .replace(
      /\{\{performanceBonusShares\}\}/g,
      offer.performanceBonusShares
        ? formatSharesRange(offer.performanceBonusShares, offer.performanceBonusSharesMax)
        : "",
    )
    .replace(
      /\{\{performanceBonusSharesMax\}\}/g,
      offer.performanceBonusSharesMax?.toLocaleString("pt-BR") || "",
    )
    .replace(/\{\{performanceBonusSharesValue\}\}/g, String(offer.performanceBonusShares || 0))
    .replace(
      /\{\{performanceBonusSharesMaxValue\}\}/g,
      String(offer.performanceBonusSharesMax || 0),
    );
}

/**
 * Renders a template preview with example data (all conditionals true).
 */
export function renderOfferTemplatePreview(htmlContent: string): string {
  const processed = htmlContent.replace(
    /\{\{#if (\w+)\}\}([\s\S]*?)\{\{\/if\}\}/g,
    (_, _key, content) => content,
  );

  return processed
    .replace(/\{\{candidateName\}\}/g, "João Silva")
    .replace(/\{\{jobTitle\}\}/g, "Desenvolvedor Full Stack")
    .replace(/\{\{salaryBase\}\}/g, "R$ 15.000")
    .replace(/\{\{equityShares\}\}/g, "1.000")
    .replace(/\{\{signingBonus\}\}/g, "R$ 10.000")
    .replace(/\{\{performanceBonus\}\}/g, "R$ 5.000")
    .replace(/\{\{salaryBaseValue\}\}/g, "15000")
    .replace(/\{\{equitySharesValue\}\}/g, "1000")
    .replace(/\{\{signingBonusValue\}\}/g, "10000")
    .replace(/\{\{performanceBonusValue\}\}/g, "5000")
    .replace(/\{\{performanceBonusShares\}\}/g, "500 – 1.000 ações")
    .replace(/\{\{performanceBonusSharesMax\}\}/g, "1.000")
    .replace(/\{\{performanceBonusSharesValue\}\}/g, "500")
    .replace(/\{\{performanceBonusSharesMaxValue\}\}/g, "1000");
}
